import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  FlaskConical, Loader2, Play, Plus, CheckCircle2, XCircle, ShieldCheck, ShieldAlert, X, Pencil, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const STATUS_STYLE = {
  valid: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300',
  broken: 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300',
  empty: 'bg-muted/40 border-border/40 text-muted-foreground',
};

export default function GoldenTests() {
  const canManage = useCan('measures:certify');
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [tests, setTests] = useState([]);
  const [results, setResults] = useState({}); // learning_id -> {status, missing}
  const [gate, setGate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ question: '', measures: '', dimensions: '' });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ question: '', measures: '', dimensions: '' });
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.getSemanticModels().then((r) => {
      const ms = r.models || r || [];
      setModels(ms);
      if (ms.length && !modelId) setModelId(ms[0].model_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const [t, g] = await Promise.all([
        api.getGoldenTests(modelId),
        api.getGoldenGate(modelId).catch(() => null),
      ]);
      setTests(t.golden_tests || []);
      setGate(g);
    } catch (e) {
      toast.error(e.message || 'Failed to load golden tests');
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => { load(); setResults({}); }, [load]);

  const runSuite = async () => {
    setRunning(true);
    try {
      const r = await api.runGoldenTests(modelId);
      const map = {};
      (r.results || []).forEach((x) => { map[x.learning_id] = x; });
      setResults(map);
      setGate({ gate_ok: r.gate_ok, has_run: true, counts: r.counts, total: r.total, broken: r.broken,
        message: r.gate_ok ? 'All golden tests pass — safe to publish.' : `${r.broken} golden test(s) broken — review before publishing.` });
      toast.success(`Ran ${r.total} golden test${r.total === 1 ? '' : 's'} — ${r.counts.broken || 0} broken`);
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const addGolden = async () => {
    setSaving(true);
    try {
      const measures = form.measures.split(',').map(s => s.trim()).filter(Boolean);
      const dimensions = form.dimensions.split(',').map(s => s.trim()).filter(Boolean);
      if (!form.question.trim() || (!measures.length && !dimensions.length)) {
        throw new Error('Question and at least one measure or dimension are required');
      }
      await api.createGoldenTest({ question: form.question.trim(), model_id: modelId, measures, dimensions });
      setForm({ question: '', measures: '', dimensions: '' });
      setShowNew(false);
      toast.success('Golden question added');
      load();
    } catch (e) { toast.error(e.message || 'Could not add'); }
    finally { setSaving(false); }
  };

  const counts = gate?.counts || {};

  const startEdit = (t) => {
    const p = t.plan || {};
    setEditId(t.learning_id);
    setEditForm({
      question: t.question || '',
      measures: (p.measures || []).join(', '),
      dimensions: (p.dimensions || []).join(', '),
    });
  };

  const saveEdit = async () => {
    setBusyId(editId);
    try {
      const measures = editForm.measures.split(',').map(s => s.trim()).filter(Boolean);
      const dimensions = editForm.dimensions.split(',').map(s => s.trim()).filter(Boolean);
      if (!editForm.question.trim() || (!measures.length && !dimensions.length)) {
        throw new Error('Question and at least one measure or dimension are required');
      }
      await api.updateGoldenTest(editId, { question: editForm.question.trim(), measures, dimensions });
      setResults(r => { const n = { ...r }; delete n[editId]; return n; });
      setEditId(null);
      toast.success('Golden test updated');
      load();
    } catch (e) { toast.error(e.message || 'Could not update'); }
    finally { setBusyId(null); }
  };

  const removeGolden = async (t) => {
    setBusyId(t.learning_id);
    try {
      await api.deleteGoldenTest(t.learning_id);
      toast.success('Golden test removed');
      load();
    } catch (e) { toast.error(e.message || 'Could not remove'); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="golden-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Label className="text-sm text-muted-foreground">Model</Label>
        <select value={modelId} onChange={(e) => setModelId(e.target.value)}
          className="h-9 rounded-md border border-border/50 bg-background px-3 text-sm" data-testid="golden-model-select">
          {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
        </select>
        {canManage && (
          <>
            <Button size="sm" onClick={runSuite} disabled={running || !tests.length} className="gap-1 text-xs h-8" data-testid="golden-run">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run tests
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowNew(v => !v)} className="gap-1 text-xs h-8" data-testid="golden-add">
              <Plus className="w-3.5 h-3.5" /> Add golden question
            </Button>
          </>
        )}
        {gate && (
          <Badge variant="outline"
            className={`text-[10px] gap-1 ${gate.gate_ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300' : 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300'}`}
            title={gate.message} data-testid="golden-gate">
            {gate.gate_ok ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
            {!gate.has_run ? 'Not run yet' : gate.gate_ok ? 'Safe to publish' : `${gate.broken} broken`}
          </Badge>
        )}
      </div>

      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
        Golden questions are the expected question→answer mappings the system must keep getting right (from accepted
        corrections, promoted learning, and steward-authored ones). Run the suite before publishing a semantic change —
        a broken test means a measure/dimension it relies on no longer resolves.
      </p>

      {showNew && (
        <div className="rounded-xl border border-border/40 bg-card/30 p-3 mb-4 space-y-2" data-testid="golden-new-form">
          <input type="text" value={form.question} onChange={(e) => setForm(f => ({ ...f, question: e.target.value }))}
            placeholder="Question, e.g. Written premium by line of business"
            className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs" data-testid="golden-new-question" />
          <div className="grid sm:grid-cols-2 gap-2">
            <input type="text" value={form.measures} onChange={(e) => setForm(f => ({ ...f, measures: e.target.value }))}
              placeholder="Measures (comma-separated field names)"
              className="h-8 px-2 rounded border border-border/40 bg-background text-xs" data-testid="golden-new-measures" />
            <input type="text" value={form.dimensions} onChange={(e) => setForm(f => ({ ...f, dimensions: e.target.value }))}
              placeholder="Dimensions (comma-separated field names)"
              className="h-8 px-2 rounded border border-border/40 bg-background text-xs" data-testid="golden-new-dimensions" />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={addGolden} disabled={saving} className="h-8 text-xs" data-testid="golden-new-submit">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowNew(false)} className="h-8 text-xs"><X className="w-3 h-3" /></Button>
          </div>
        </div>
      )}

      {gate?.has_run && (
        <div className="flex items-center gap-3 text-xs mb-3" data-testid="golden-run-summary">
          <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> {counts.valid || 0} valid</span>
          <span className="flex items-center gap-1 text-rose-600"><XCircle className="w-3.5 h-3.5" /> {counts.broken || 0} broken</span>
          {gate.last_run_at && <span className="text-muted-foreground">last run {new Date(gate.last_run_at).toLocaleString()}</span>}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : tests.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="golden-empty">
          No golden questions yet. Accept a correction in AI Explore, promote a learning candidate, or add one above.
        </div>
      ) : (
        <div className="space-y-2" data-testid="golden-list">
          {tests.map((t) => {
            const res = results[t.learning_id];
            const p = t.plan || {};
            return (
              <div key={t.learning_id} className="rounded-lg border border-border/40 bg-card/30 p-3" data-testid={`golden-row-${t.learning_id}`}>
                {editId === t.learning_id ? (
                  <div className="space-y-2" data-testid={`golden-edit-form-${t.learning_id}`}>
                    <input type="text" value={editForm.question} onChange={(e) => setEditForm(f => ({ ...f, question: e.target.value }))}
                      placeholder="Question" className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs" data-testid={`golden-edit-question-${t.learning_id}`} />
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input type="text" value={editForm.measures} onChange={(e) => setEditForm(f => ({ ...f, measures: e.target.value }))}
                        placeholder="Measures (comma-separated)" className="h-8 px-2 rounded border border-border/40 bg-background text-xs" data-testid={`golden-edit-measures-${t.learning_id}`} />
                      <input type="text" value={editForm.dimensions} onChange={(e) => setEditForm(f => ({ ...f, dimensions: e.target.value }))}
                        placeholder="Dimensions (comma-separated)" className="h-8 px-2 rounded border border-border/40 bg-background text-xs" data-testid={`golden-edit-dimensions-${t.learning_id}`} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={saveEdit} disabled={busyId === t.learning_id} className="h-8 text-xs" data-testid={`golden-edit-save-${t.learning_id}`}>
                        {busyId === t.learning_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="h-8 text-xs" data-testid={`golden-edit-cancel-${t.learning_id}`}><X className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <FlaskConical className="w-4 h-4 text-violet-500 flex-shrink-0" />
                      <span className="font-medium text-sm truncate">{t.question}</span>
                      {t.source && <Badge variant="outline" className="text-[9px]">{t.source}</Badge>}
                      {res && (
                        <Badge variant="outline" className={`text-[9px] ${STATUS_STYLE[res.status] || ''}`} data-testid={`golden-status-${t.learning_id}`}>
                          {res.status === 'valid' ? 'Pass' : res.status === 'broken' ? 'Broken' : 'Empty'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {(p.measures || []).map(m => String(m).replace(/_/g, ' ')).join(', ')}
                      {(p.dimensions || []).length > 0 && ` by ${(p.dimensions).map(d => String(d).replace(/_/g, ' ')).join(', ')}`}
                    </p>
                    {res?.missing?.length > 0 && (
                      <p className="text-[11px] text-rose-600 mt-0.5">missing: {res.missing.join(', ')}</p>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(t)} title="Edit golden test" data-testid={`golden-edit-${t.learning_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:text-rose-700" onClick={() => removeGolden(t)} disabled={busyId === t.learning_id} title="Remove golden test" data-testid={`golden-delete-${t.learning_id}`}>
                        {busyId === t.learning_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  )}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { GoldenTests };
